import { useEffect, useMemo, useState } from "react";
import {
  CheckCircle2,
  Plus,
  Trash2,
  PackageSearch,
  UserPlus,
  Archive,
  AlertTriangle,
  XCircle,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { eur, pct, margeText, margeBadge, UNITES } from "../../lib/format";
import { useViewRole } from "../../context/ViewRoleContext";
import CycleVieDossier from "./CycleVieDossier";
import ActionLogDialog from "../actions/ActionLogDialog";
import ClientQuickCreate from "./ClientQuickCreate";
import Dossier360 from "./Dossier360";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from "../ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

const STATUTS = ["Brouillon", "Envoyé", "Négociation", "Validé / Accepté", "Refusé"];

function emptyLine() {
  return { id: crypto.randomUUID(), prestation_id: null, nom: "", famille: "", quantite: 1, unite: "u (pièce)", cout_unitaire_ht: 0, prix_unitaire_ht: 0, tva_pct: 20 };
}

export default function DevisBuilder({ open, onOpenChange, clients, catalogue, devis, onSaved, onDossierUpdated, readOnly = false }) {
  const { canSeeMargins } = useViewRole();
  const isHistorical = devis?.source === "import";
  const isArchived = Boolean(
    devis?.archivee ||
    devis?.statut === "Clôturé" ||
    devis?.statut === "Refusé"
  );
  const isReadOnly = Boolean(readOnly || isArchived);
  const [titre, setTitre] = useState("");
  const [clientId, setClientId] = useState("");
  const [availableClients, setAvailableClients] = useState(clients || []);
  const [clientCreateOpen, setClientCreateOpen] = useState(false);
  const [chantierNom, setChantierNom] = useState("");
  const [famille, setFamille] = useState("");
  const [statut, setStatut] = useState("Brouillon");
  const [remise, setRemise] = useState(0);
  const [lignes, setLignes] = useState([emptyLine()]);
  const [relanceOpen, setRelanceOpen] = useState(false);
  const [clotureOpen, setClotureOpen] = useState(false);
  const [clotureCheck, setClotureCheck] = useState(null);
  const [clotureLoading, setClotureLoading] = useState(false);
  const [clotureSaving, setClotureSaving] = useState(false);
  const [refusOpen, setRefusOpen] = useState(false);
  const [refusSaving, setRefusSaving] = useState(false);

  const ouvrirCloture = async () => {
    if (!devis?.id) return;

    setClotureOpen(true);
    setClotureCheck(null);
    setClotureLoading(true);

    try {
      const { data } = await api.get(`/devis/${devis.id}/cloture-check`);
      setClotureCheck(data);
    } catch (error) {
      setClotureOpen(false);
      toast.error(
        error?.response?.data?.detail || "Impossible de vérifier la clôture"
      );
    } finally {
      setClotureLoading(false);
    }
  };

  const confirmerCloture = async () => {
    if (!devis?.id || !clotureCheck?.can_cloturer) return;

    setClotureSaving(true);

    try {
      const { data } = await api.post(`/devis/${devis.id}/cloturer`, {
        confirmation: true,
        confirmer_avertissements: (clotureCheck.warnings || []).length > 0,
      });

      toast.success(data?.message || "Dossier clôturé et archivé");
      setClotureOpen(false);
      setClotureCheck(null);
      onOpenChange(false);

      if (onSaved) onSaved(data?.devis);
    } catch (error) {
      const detail = error?.response?.data?.detail;

      if (detail?.warnings) {
        setClotureCheck((prev) => ({
          ...(prev || {}),
          warnings: detail.warnings.map((label) => ({
            code: "server",
            label,
          })),
        }));
      }

      toast.error(
        detail?.message ||
          (typeof detail === "string"
            ? detail
            : "Impossible de clôturer le dossier")
      );
    } finally {
      setClotureSaving(false);
    }
  };

  useEffect(() => {
    setAvailableClients(clients || []);
  }, [clients]);

  useEffect(() => {
    if (!open) return;
    if (devis) {
      setTitre(devis.titre || "");
      setClientId(devis.client_id || "");
      setChantierNom(devis.chantier_nom || "");
      setFamille(devis.famille || "");
      setStatut(devis.statut || "Brouillon");
      setRemise(devis.remise_pct || 0);
      setLignes((devis.lignes || []).map((l) => ({ ...l })));
    } else {
      setTitre(""); setClientId(""); setChantierNom(""); setFamille(""); setStatut("Brouillon"); setRemise(0);
      setLignes([emptyLine()]);
    }
  }, [open, devis]);

  const updateLine = (id, patch) => setLignes((prev) => prev.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  const removeLine = (id) => setLignes((prev) => prev.filter((l) => l.id !== id));
  const addLine = () => setLignes((prev) => [...prev, emptyLine()]);

  const addFromCatalogue = (prestaId) => {
    const p = catalogue.find((c) => c.id === prestaId);
    if (p && !famille && p.famille) setFamille(p.famille);
    if (!p) return;
    setLignes((prev) => [
      ...prev.filter((l) => l.nom || l.prestation_id),
      { id: crypto.randomUUID(), prestation_id: p.id, nom: p.nom, famille: p.famille, quantite: 1, unite: p.unite, cout_unitaire_ht: p.cout_unitaire_ht, prix_unitaire_ht: p.prix_unitaire_ht, tva_pct: p.tva_pct },
    ]);
    toast.success(`${p.nom} ajoutée`);
  };

  const totals = useMemo(() => {
    const factor = 1 - (Number(remise) || 0) / 100;
    let ht = 0, cout = 0, tva = 0;
    lignes.forEach((l) => {
      const v = (Number(l.quantite) || 0) * (Number(l.prix_unitaire_ht) || 0);
      const c = (Number(l.quantite) || 0) * (Number(l.cout_unitaire_ht) || 0);
      ht += v; cout += c; tva += v * factor * (Number(l.tva_pct) || 0) / 100;
    });
    const htNet = ht * factor;
    const marge = htNet - cout;
    return { htNet, cout, tva, ttc: htNet + tva, marge, taux: htNet ? (marge / htNet) * 100 : 0 };
  }, [lignes, remise]);

  const demanderRefus = () => {
    if (isReadOnly) {
      toast.info("Ce dossier est archivé et consultable uniquement.");
      return;
    }

    if (!devis?.id) {
      toast.error("Le refus est disponible uniquement pour un dossier existant.");
      return;
    }

    setRefusOpen(true);
  };

  const confirmerRefus = async () => {
    if (!devis?.id) return;

    setRefusSaving(true);

    try {
      const { data } = await api.post(`/devis/${devis.id}/refuser`, {
        confirmation: true,
      });

      toast.success(data?.message || "Devis refusé et archivé.");

      setRefusOpen(false);

      if (data?.devis && onDossierUpdated) {
        onDossierUpdated(data.devis);
      }

      onOpenChange(false);

      if (onSaved) onSaved(data?.devis);
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
        "Impossible de refuser ce devis."
      );
    } finally {
      setRefusSaving(false);
    }
  };

  const save = async () => {
    if (isReadOnly) {
      toast.info("Ce dossier est archivé et consultable uniquement.");
      return;
    }

    if (!titre) return toast.error("Titre requis");
    const client = availableClients.find((c) => c.id === clientId);
    const payload = {
      titre, client_id: clientId || null, client_nom: client?.nom || "",
      chantier_nom: chantierNom, statut, remise_pct: Number(remise) || 0,
      lignes: lignes.filter((l) => l.nom).map((l) => ({
        id: l.id, prestation_id: l.prestation_id, nom: l.nom, famille: l.famille,
        quantite: Number(l.quantite) || 0, unite: l.unite,
        cout_unitaire_ht: Number(l.cout_unitaire_ht) || 0, prix_unitaire_ht: Number(l.prix_unitaire_ht) || 0,
        tva_pct: Number(l.tva_pct) || 0,
      })),
    };
    try {
      if (devis) { await api.put(`/devis/${devis.id}`, payload); toast.success("Devis mis à jour"); }
      else { await api.post("/devis", payload); toast.success("Devis créé"); }
      onOpenChange(false);
      onSaved && onSaved();
    } catch { toast.error("Erreur lors de l'enregistrement"); }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="crm-dossier-shell bg-[#F5F7FA] border-[#E2E8F0] text-[#172033] w-full sm:max-w-4xl overflow-y-auto p-0">
        <div className="crm-dossier-header p-6 sticky top-0 z-10">
          <SheetHeader>
            <SheetTitle className="font-head text-xl text-[#172033] font-bold">{devis ? `Modifier ${devis.numero}` : "Nouveau devis chiffré"}</SheetTitle>
          </SheetHeader>
        </div>

        <div className="p-6 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="crm-dossier-muted text-xs">Titre du devis</Label>
              <Input data-testid="devis-input-titre" value={titre} onChange={(e) => setTitre(e.target.value)} className="crm-dossier-field" />
            </div>
            <div className="space-y-1.5">
              <Label className="crm-dossier-muted text-xs">Client</Label>
              <div className="flex items-center gap-2">
                <div className="flex-1 min-w-0">
                  <Select value={clientId} onValueChange={setClientId}>
                    <SelectTrigger data-testid="devis-select-client" className="crm-dossier-field"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                    <SelectContent className="crm-dossier-field text-[#172033]">
                      {availableClients.map((c) => <SelectItem key={c.id} value={c.id}>{c.nom}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {!isHistorical && (
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    data-testid="devis-new-client"
                    onClick={() => setClientCreateOpen(true)}
                    title="Créer un nouveau client"
                    className="shrink-0 border-amber-500/30 bg-[#111827] text-amber-400 hover:bg-amber-500/10 hover:text-amber-300"
                  >
                    <UserPlus className="h-4 w-4" />
                  </Button>
                )}
              </div>
              {!clientId && !isHistorical && <p className="text-[11px] crm-dossier-muted">Pas encore de client ? Créez-le ici sans quitter le devis.</p>}
            </div>
            <div className="space-y-1.5">
              <Label className="crm-dossier-muted text-xs">Nom du chantier</Label>
              <Input value={chantierNom} onChange={(e) => setChantierNom(e.target.value)} className="crm-dossier-field" />
            </div>
            <div>
          <label>Famille</label>
          <select
            data-testid="devis-select-famille"
            value={famille}
            onChange={(e) => setFamille(e.target.value)}
          >
            <option value="">— Aucune famille —</option>
            {Array.from(
              new Set(
                (catalogue || [])
                  .map((p) => p.famille)
                  .filter((f) => typeof f === "string" && f.trim())
              )
            )
              .sort((a, b) => a.localeCompare(b, "fr"))
              .map((f) => (
                <option key={f} value={f}>{f}</option>
              ))}
          </select>
        </div>

        <div className="space-y-1.5">
              <Label className="crm-dossier-muted text-xs">Statut</Label>
              <Select
                value={statut}
                onValueChange={(value) => {
                  if (value === "Refusé") {
                    demanderRefus();
                  } else {
                    setStatut(value);
                  }
                }}
              >
                <SelectTrigger data-testid="devis-select-statut" className="crm-dossier-field">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="crm-dossier-field text-[#172033]">
                  {STATUTS.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {isHistorical && (
            <div className="rounded-lg border border-sky-500/30 bg-sky-500/10 px-4 py-3 text-sm text-sky-200">
              <strong>Dossier historique</strong> — les montants issus de l’ancien fichier Excel sont conservés. Les lignes historiques sont protégées ; vous pouvez modifier les informations du dossier sans réécrire son historique financier.
            </div>
          )}

            {devis && (
              <>
                <CycleVieDossier
                  devis={devis}
                  readOnly={isReadOnly}
                  onUpdated={(updated) => {
                    if (updated && onDossierUpdated) {
                      onDossierUpdated(updated);
                    }
                  }}
                />
                <Dossier360
                  devis={devis}
                  canSeeMargins={canSeeMargins}
                />
              </>
            )}

          {!isHistorical && <div className="flex items-center gap-3">
            <div className="flex-1">
              <Select value="" onValueChange={addFromCatalogue}>
                <SelectTrigger data-testid="devis-catalogue-select" className="bg-[#111827] border-amber-500/30 text-amber-400">
                  <span className="inline-flex items-center gap-2"><PackageSearch className="h-4 w-4" /> Ajouter depuis le catalogue</span>
                </SelectTrigger>
                <SelectContent className="crm-dossier-field text-[#172033] max-h-72">
                  {catalogue.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.code} — {p.nom} ({eur(p.prix_unitaire_ht)}/{p.unite})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" data-testid="devis-add-line" onClick={addLine} className="border-[#1E293B] bg-[#111827] hover:bg-[#1A2234] text-slate-200">
              <Plus className="h-4 w-4 mr-1" /> Ligne libre
            </Button>
          </div>}

          <div className="rounded-xl bg-[#111827] border crm-dossier-table">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left uppercase tracking-wider crm-dossier-muted border-b border-[#1E293B]">
                    <th className="px-3 py-2 font-medium min-w-[160px]">Désignation</th>
                    <th className="px-2 py-2 font-medium text-right">Qté</th>
                    <th className="px-2 py-2 font-medium">Unité</th>
                    {canSeeMargins && <th className="px-2 py-2 font-medium text-right">Coût HT</th>}
                    <th className="px-2 py-2 font-medium text-right">PU HT</th>
                    <th className="px-2 py-2 font-medium text-right">TVA</th>
                    <th className="px-2 py-2 font-medium text-right">Total HT</th>
                    {canSeeMargins && <th className="px-2 py-2 font-medium text-right">Marge</th>}
                    <th className="px-2 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {lignes.map((l) => {
                    const totalLigne = (Number(l.quantite) || 0) * (Number(l.prix_unitaire_ht) || 0);
                    const coutLigne = (Number(l.quantite) || 0) * (Number(l.cout_unitaire_ht) || 0);
                    const margeLigne = totalLigne - coutLigne;
                    const tauxLigne = totalLigne ? (margeLigne / totalLigne) * 100 : 0;
                    return (
                      <tr key={l.id} className="border-b border-[#1E293B]/50" data-testid="devis-line-row">
                        <td className="px-3 py-1.5">
                          <input value={l.nom} disabled={isHistorical} onChange={(e) => updateLine(l.id, { nom: e.target.value })} placeholder="Désignation" className="w-full bg-transparent focus:outline-none text-[#172033]" />
                        </td>
                        <td className="px-2 py-1.5">
                          <input type="number" value={l.quantite} disabled={isHistorical} onChange={(e) => updateLine(l.id, { quantite: e.target.value })} className="w-16 crm-dossier-field rounded px-1.5 py-1 text-right font-mono-metric focus:outline-none focus:border-amber-500/40" />
                        </td>
                        <td className="px-2 py-1.5">
                          <select value={l.unite} disabled={isHistorical} onChange={(e) => updateLine(l.id, { unite: e.target.value })} className="crm-dossier-field rounded px-1 py-1 text-[#42526b] focus:outline-none">
                            {UNITES.map((u) => <option key={u} value={u}>{u}</option>)}
                          </select>
                        </td>
                        {canSeeMargins && (
                          <td className="px-2 py-1.5">
                            <input type="number" value={l.cout_unitaire_ht} disabled={isHistorical} onChange={(e) => updateLine(l.id, { cout_unitaire_ht: e.target.value })} className="w-20 crm-dossier-field rounded px-1.5 py-1 text-right font-mono-metric crm-dossier-warning focus:outline-none focus:border-amber-500/40" />
                          </td>
                        )}
                        <td className="px-2 py-1.5">
                          <input type="number" value={l.prix_unitaire_ht} disabled={isHistorical} onChange={(e) => updateLine(l.id, { prix_unitaire_ht: e.target.value })} className="w-20 crm-dossier-field rounded px-1.5 py-1 text-right font-mono-metric focus:outline-none focus:border-amber-500/40" />
                        </td>
                        <td className="px-2 py-1.5">
                          <input type="number" value={l.tva_pct} disabled={isHistorical} onChange={(e) => updateLine(l.id, { tva_pct: e.target.value })} className="w-14 crm-dossier-field rounded px-1.5 py-1 text-right font-mono-metric focus:outline-none focus:border-amber-500/40" />
                        </td>
                        <td className="px-2 py-1.5 text-right font-mono-metric font-semibold text-[#172033]">{eur(totalLigne)}</td>
                        {canSeeMargins && (
                          <td className={`px-2 py-1.5 text-right font-mono-metric font-semibold ${margeText(tauxLigne)}`}>{pct(tauxLigne)}</td>
                        )}
                        <td className="px-2 py-1.5 text-right">
                          <button disabled={isHistorical} onClick={() => removeLine(l.id)} className="crm-dossier-muted hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="crm-dossier-muted text-xs">Remise commerciale globale (%)</Label>
              <Input type="number" data-testid="devis-input-remise" value={remise} disabled={isHistorical} onChange={(e) => setRemise(e.target.value)} className="crm-dossier-field font-mono-metric" />
            </div>
            <div className="rounded-xl crm-dossier-summary p-4 space-y-2" data-testid="devis-summary">
              <Row label="Total HT" value={eur(totals.htNet)} />
              {canSeeMargins && <Row label="Coût total HT" value={eur(totals.cout)} className="crm-dossier-warning" />}
              <Row label="TVA" value={eur(totals.tva)} className="text-slate-400" />
              <div className="flex items-center justify-between pt-2 border-t border-[#1E293B]">
                <span className="text-sm font-semibold">Total TTC</span>
                <span className="font-mono-metric text-lg font-bold text-[#172033]">{eur(totals.ttc)}</span>
              </div>
              {canSeeMargins && (
                <div className="flex items-center justify-between pt-1">
                  <span className="text-sm text-slate-400">Marge brute</span>
                  <div>
                    <span className="font-mono-metric font-bold crm-dossier-success">{eur(totals.marge)}</span>
                    <span className={`ml-2 text-[11px] font-semibold px-2 py-0.5 rounded border ${margeBadge(totals.taux)}`}>{pct(totals.taux)}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="px-6 py-3 border-t border-[#E2E8F0] bg-white flex items-center justify-between">
          <div className="text-xs crm-dossier-muted">
            Une action commerciale réalisée peut être enregistrée directement depuis ce dossier.
          </div>

          {devis && !devis.archivee && devis.statut !== "Clôturé" && (
            <Button
              type="button"
              data-testid="devis-close-button"
              variant="outline"
              onClick={ouvrirCloture}
              className="border-amber-500/30 text-amber-600 hover:bg-amber-500/10"
            >
              <Archive className="h-4 w-4 mr-2" />
              Clôturer et archiver
            </Button>
          )}

          <Button
            type="button"
            data-testid="devis-quick-relance"
            variant="outline"
            onClick={() => setRelanceOpen(true)}
            className="border-emerald-500/30 crm-dossier-success hover:bg-emerald-500/10 hover:text-emerald-300"
          >
            <CheckCircle2 className="h-4 w-4 mr-2" />
            Relance effectuée
          </Button>
        </div>

        <SheetFooter className="p-6 border-t border-[#1E293B] sticky bottom-0 bg-[#0B0F17]">
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-slate-400">Annuler</Button>
          <Button data-testid="devis-save-button" onClick={save} className="bg-amber-500 hover:bg-amber-600 text-[#0B0F17] font-semibold">
            {devis ? "Enregistrer les modifications" : "Créer le devis"}
          </Button>
        </SheetFooter>
      </SheetContent>

      <ClientQuickCreate
        open={clientCreateOpen}
        onOpenChange={setClientCreateOpen}
        onCreated={(client) => {
          setAvailableClients((prev) => [...prev.filter((c) => c.id !== client.id), client]);
          setClientId(client.id);
        }}
      />

      <Sheet open={refusOpen} onOpenChange={(value) => !refusSaving && setRefusOpen(value)}>
        <SheetContent className="w-full sm:max-w-xl bg-white text-[#172033]">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2 text-xl text-red-700">
              <AlertTriangle className="h-5 w-5" />
              Refuser ce devis ?
            </SheetTitle>
          </SheetHeader>

          <div className="px-6 py-6 space-y-5">
            <div className="rounded-xl border border-red-200 bg-red-50 p-4">
              <div className="font-semibold text-red-800">
                {devis?.numero || "Dossier"}
              </div>
              <div className="text-sm text-red-700 mt-1">
                {devis?.client_nom || "Client non renseigné"}
              </div>
            </div>

            <div className="space-y-3 text-sm text-slate-600">
              <p>
                Le refus mettra définitivement fin au parcours commercial de ce dossier.
              </p>

              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-2">
                <div>✓ Le statut passera à <strong>Refusé</strong></div>
                <div>✓ Le refus sera horodaté et attribué au commercial</div>
                <div>✓ Les tâches et relances ouvertes seront annulées</div>
                <div>✓ Le dossier sera automatiquement archivé</div>
                <div>✓ Le dossier restera intégralement consultable dans Archives</div>
              </div>

              <p className="font-medium text-slate-800">
                Cette opération ne supprime aucune donnée.
              </p>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setRefusOpen(false)}
                disabled={refusSaving}
              >
                Annuler
              </Button>

              <Button
                type="button"
                onClick={confirmerRefus}
                disabled={refusSaving}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {refusSaving ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <XCircle className="h-4 w-4 mr-2" />
                )}
                {refusSaving ? "Archivage..." : "Confirmer le refus"}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <ActionLogDialog
        open={relanceOpen}
        onOpenChange={setRelanceOpen}
        context={{
          client_id: clientId || devis?.client_id || null,
          client_nom:
            availableClients.find((c) => c.id === clientId)?.nom ||
            devis?.client_nom ||
            "",
          project_id: devis?.project_id || null,
          devis_id: devis?.id || null,
          preset_type: "relance",
        }}
        dossiers={[]}
        onSaved={() => {
          setRelanceOpen(false);
          window.dispatchEvent(new CustomEvent("crm:data-changed"));
        }}
      />

      <Sheet open={clotureOpen} onOpenChange={setClotureOpen}>
        <SheetContent className="w-full sm:max-w-xl bg-white text-[#172033]">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2 text-xl">
              <Archive className="h-5 w-5 text-amber-500" />
              Clôturer et archiver le dossier
            </SheetTitle>
          </SheetHeader>

          <div className="px-6 py-5 space-y-5">
            {clotureLoading ? (
              <div className="flex items-center justify-center py-10 text-slate-500">
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                Vérification du dossier...
              </div>
            ) : clotureCheck ? (
              <>
                <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
                  <div className="text-sm text-slate-500">Dossier</div>
                  <div className="font-semibold text-lg">
                    {clotureCheck.numero || devis?.numero || "Dossier"}
                  </div>
                </div>

                {clotureCheck.blocking?.length > 0 && (
                  <div className="rounded-xl border border-red-200 bg-red-50 p-4">
                    <div className="flex items-center gap-2 font-semibold text-red-700 mb-2">
                      <XCircle className="h-5 w-5" />
                      Clôture impossible
                    </div>

                    <div className="space-y-2">
                      {clotureCheck.blocking.map((item, index) => (
                        <div key={index} className="text-sm text-red-700">
                          • {item.label || item}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {clotureCheck.checks?.length > 0 && (
                  <div>
                    <div className="font-semibold mb-3">Contrôles validés</div>

                    <div className="space-y-2">
                      {clotureCheck.checks.map((item, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-2 text-sm text-emerald-700"
                        >
                          <CheckCircle2 className="h-4 w-4 shrink-0" />
                          {item.label}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {clotureCheck.warnings?.length > 0 && (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                    <div className="flex items-center gap-2 font-semibold text-amber-700 mb-2">
                      <AlertTriangle className="h-5 w-5" />
                      Points à confirmer
                    </div>

                    <div className="space-y-2">
                      {clotureCheck.warnings.map((item, index) => (
                        <div key={index} className="text-sm text-amber-800">
                          • {item.label || item}
                        </div>
                      ))}
                    </div>

                    <p className="text-xs text-amber-700 mt-3">
                      Ces points n'empêchent pas la clôture, mais seront enregistrés
                      comme avertissements.
                    </p>
                  </div>
                )}

                {clotureCheck.open_tasks?.length > 0 && (
                  <div className="rounded-lg bg-slate-50 p-3 text-sm text-slate-600">
                    <strong>{clotureCheck.open_tasks.length}</strong>{" "}
                    tâche(s) encore ouverte(s) seront annulées automatiquement
                    lors de la clôture.
                  </div>
                )}

                {clotureCheck.can_cloturer && (
                  <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800">
                    <strong>Validation finale :</strong> la clôture sera horodatée,
                    journalisée et le dossier sortira des dossiers actifs.
                  </div>
                )}
              </>
            ) : null}
          </div>

          <SheetFooter className="p-6 border-t border-slate-200 bg-white">
            <Button
              variant="ghost"
              onClick={() => setClotureOpen(false)}
              disabled={clotureSaving}
            >
              Annuler
            </Button>

            <Button
              onClick={confirmerCloture}
              disabled={
                clotureSaving ||
                clotureLoading ||
                !clotureCheck?.can_cloturer
              }
              className="bg-amber-500 hover:bg-amber-600 text-[#172033] font-semibold"
            >
              {clotureSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Clôture en cours...
                </>
              ) : (
                <>
                  <Archive className="h-4 w-4 mr-2" />
                  Confirmer et archiver
                </>
              )}
            </Button>
          </SheetFooter>
        </SheetContent>
    </Sheet>
    </Sheet>
  );
}

function Row({ label, value, className = "" }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-slate-400">{label}</span>
      <span className={`font-mono-metric font-semibold ${className || "text-[#172033]"}`}>{value}</span>
    </div>
  );
}
